import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  VStack,
  Box,
  Text,
  Icon,
  useColorModeValue,
  Checkbox,
  Select,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { AlertCircle, Type, AlignLeft, User as UserIcon, Briefcase, Link as LinkIcon } from 'lucide-react';
import { Issue, User, Department, Rock } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { useModalStyles } from '../../../../utils/modal.styles';

interface AddIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: any) => Promise<void>;
  currentUserId?: string;
  initialRelatedRockId?: string | null;
  submitButtonText?: string; // Custom button text (e.g., "Add Issue and Move Rock to Off-Track")
  context?: 'moving-to-off-track' | 'adding-to-on-track'; // Context for warning message
  isCompany?: boolean; // Set to true to mark as company issue by default
  modalTitle?: string; // Custom modal title
  // Edit mode props
  initialIssue?: Issue; // For edit mode
  mode?: 'create' | 'edit'; // Default 'create'
  users?: User[]; // Optional - component now uses eosUsers from AuthContext
  departments?: Department[]; // Required for edit mode with company issues
  rocks?: Rock[]; // Required to display rock name in edit mode
}

/**
 * Add Issue Modal Component
 * Uses Formik for form state and Yup for validation
 * Migrated from Tailwind to Chakra UI
 * Supports both create and edit modes
 */
export function AddIssueModal({
  isOpen,
  onClose,
  onSave,
  currentUserId,
  initialRelatedRockId,
  submitButtonText,
  context,
  isCompany = false,
  modalTitle,
  initialIssue,
  mode = 'create',
  users = [],
  departments = [],
  rocks = [],
}: AddIssueModalProps) {
  const { eosUsers = [] } = useAuth();
  const styles = useModalStyles();
  
  // Alert colors (kept specific for warning context)
  const bgColor = useColorModeValue('orange.100', 'orange.900');
  const borderColor = useColorModeValue('orange.200', 'orange.700');
  const iconColor = useColorModeValue('orange.600', 'orange.300');
  const textColor = useColorModeValue('orange.800', 'orange.200');

  const isEditMode = mode === 'edit';
  const effectiveSubmitText = submitButtonText || (isEditMode ? 'Save Changes' : 'Add Issue');
  const effectiveTitle = modalTitle || (isEditMode ? 'Edit Issue' : 'Add New Issue');

  // Validation schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Issue name is required')
      .min(3, 'Issue name must be at least 3 characters'),
    description: Yup.string()
      .required('Description is required')
      .min(10, 'Description must be at least 10 characters'),
    status: Yup.string().oneOf(['open', 'in-progress', 'solved']).optional(),
    assignedToUserId: Yup.string().nullable().optional(),
    departmentId: Yup.string().nullable().optional(),
  });

  const initialValues = isEditMode && initialIssue
    ? {
        name: initialIssue.name || '',
        description: initialIssue.description || '',
        status: initialIssue.status || 'open',
        relatedRockId: initialIssue.relatedRockId || null,
        relatedMetricId: initialIssue.relatedMetricId || null,
        isCompany: initialIssue.isCompany || false,
        assignedToUserId: initialIssue.assignedToUserId || '',
        departmentId: initialIssue.departmentId || '',
      }
    : {
        name: '',
        description: '',
        status: 'open',
        relatedRockId: initialRelatedRockId || null,
        relatedMetricId: null,
        isCompany: isCompany,
        assignedToUserId: '',
        departmentId: '',
      };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onSave({
        ...values,
        ownerId: currentUserId,
      });
      onClose();
    } catch (error) {
      console.error('Error saving issue:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg={styles.modal.overlayBg} />
      <ModalContent bg={styles.modal.bg}>
        <ModalHeader bg={styles.header.bg} borderTopRadius="md">{effectiveTitle}</ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <ModalBody pb={6}>
                <VStack spacing={6} align="stretch">
                  {/* Warning message based on context */}
                  {context && (
                    <Box
                      bg={bgColor}
                      borderWidth={1}
                      borderColor={borderColor}
                      backgroundColor={bgColor}
                      borderRadius="md"
                      p={4}
                    >
                      <Box display="flex" alignItems="start" gap={3}>
                        <Icon
                          as={AlertCircle}
                          w={5}
                          h={5}
                          color={iconColor}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Box flex={1} >
                          {context === 'moving-to-off-track' ? (
                            <>
                              <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                                Issue Required for Off Track Status
                              </Text>
                              <Text fontSize="sm" color={textColor} mb={2}>
                                An issue must be created before a Rock can be moved to Off Track status.
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                Off Track Rocks require documented issues to provide visibility into blockers and help the team understand what's preventing progress.
                              </Text>
                            </>
                          ) : (
                            <>
                              <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                                This Rock has no issues - adding an issue will move Rock to Off-Track
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                Off Track Rocks require documented issues to provide visibility into blockers and help the team understand what's preventing progress.
                              </Text>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Field name="name">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched} isRequired>
                        <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Issue Name</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none" children={<Icon as={Type} color={styles.icon.default} />} />
                          <Input 
                            {...field} 
                            placeholder="Brief issue title..." 
                            bg={styles.input.bg}
                            borderColor={styles.input.borderColor}
                            _placeholder={{ color: styles.input.placeholderColor }}
                            _focus={styles.input._focus}
                          />
                        </InputGroup>
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="description">
                    {({ field, meta }: any) => (
                      <FormControl isInvalid={meta.error && meta.touched} isRequired>
                        <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Description</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Describe the issue in detail..."
                          rows={5}
                          bg={styles.input.bg}
                          borderColor={styles.input.borderColor}
                          _placeholder={{ color: styles.input.placeholderColor }}
                          _focus={styles.input._focus}
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Status field - shown in edit mode */}
                  {isEditMode && (
                    <Field name="status">
                      {({ field, meta }: any) => (
                        <FormControl isInvalid={meta.error && meta.touched}>
                          <FormLabel>Status</FormLabel>
                          <Select {...field}>
                            <option value="open">Open</option>
                            <option value="in-progress">In Progress</option>
                            <option value="solved">Solved</option>
                          </Select>
                          <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {/* Company issue checkbox - shown in create mode or when editing non-company issues */}
                  {(!isEditMode || !initialIssue?.isCompany) && (
                    <Field name="isCompany" type="checkbox">
                      {({ field }: any) => (
                        <FormControl display="flex" alignItems="center">
                          <Checkbox 
                            {...field} 
                            id="isCompany" 
                            isChecked={field.value} 
                            isDisabled={isCompany && !isEditMode}
                            colorScheme="blue"
                          >
                            Company Issue
                          </Checkbox>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {/* Company-specific fields - shown when isCompany is true */}
                  {((isEditMode && initialIssue?.isCompany) || (!isEditMode && isCompany)) && (
                    <>
                      <Field name="assignedToUserId">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched}>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Assign To</FormLabel>
                            <InputGroup>
                              <InputLeftElement pointerEvents="none" children={<Icon as={UserIcon} color={styles.icon.default} />} />
                              <Select 
                                {...field} 
                                placeholder="Select assignee (optional)"
                                pl={10}
                                bg={styles.input.bg}
                                borderColor={styles.input.borderColor}
                              >
                                <option value="">None</option>
                                {eosUsers.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.name}
                                  </option>
                                ))}
                              </Select>
                            </InputGroup>
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      <Field name="departmentId">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched}>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Department</FormLabel>
                            <InputGroup>
                              <InputLeftElement pointerEvents="none" children={<Icon as={Briefcase} color={styles.icon.default} />} />
                              <Select 
                                {...field} 
                                placeholder="Select department (optional)"
                                pl={10}
                                bg={styles.input.bg}
                                borderColor={styles.input.borderColor}
                              >
                                <option value="">None</option>
                                {departments.map((dept) => (
                                  <option key={dept.id} value={dept.id}>
                                    {dept.name}
                                  </option>
                                ))}
                              </Select>
                            </InputGroup>
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </>
                  )}

                  {/* Related Rock ID - shown in edit mode if issue is linked to a rock */}
                  {isEditMode && initialIssue?.relatedRockId && (
                    <Field name="relatedRockId">
                      {({ field }: any) => {
                        const linkedRock = rocks.find((r) => r.id === initialIssue?.relatedRockId);
                        const rockName = linkedRock ? linkedRock.rock : initialIssue?.relatedRockId || 'N/A';
                        return (
                          <FormControl>
                            <FormLabel>Linked Rock</FormLabel>
                            <Input {...field} isDisabled value={rockName} />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Rock linkage cannot be changed
                            </Text>
                          </FormControl>
                        );
                      }}
                    </Field>
                  )}
                </VStack>
              </ModalBody>

              <ModalFooter bg={styles.footer.bg} borderTopWidth="1px" borderColor={styles.footer.borderColor}>
                <Button variant="ghost" mr={3} onClick={onClose} size="lg" _hover={styles.button.ghost._hover}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                  size="lg"
                  px={8}
                  bgGradient={styles.button.primary.bgGradient}
                  _hover={{ bgGradient: styles.button.primary._hover.bgGradient, boxShadow: 'md' }}
                >
                  {effectiveSubmitText}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}

